import { Camera, Scene, Vector3 as ThreeVector3, Matrix4, Quaternion as ThreeQuaternion } from 'three';
import { AppSettingsData } from '../core/AppSettingsData';
import { SystemState } from '../core/SystemState';
import { POI } from '../poi/POI';
import { PerspectiveCamera } from 'three';
import { CameraRotationMode } from '../types';
import { DeviceInfo } from '../utils/DeviceInfo';
import { Logger } from '../core/Logger';
import { CameraChoreographer } from './CameraChoreographer';

/** Camera distance multiplier on mobile — keeps content fully visible on smaller viewports. */
const MOBILE_DISTANCE_MULTIPLIER = 1.25;

export class AdvancedPOICamera {
    private _camera: PerspectiveCamera;
    private _scene: Scene;
    private _settings: AppSettingsData;
    private _systemState: SystemState;
    private _targetPOI: POI | null = null;
    
    // For smooth turning
    private _desiredPosition: ThreeVector3 = new ThreeVector3();
    private _desiredQuaternion: ThreeQuaternion = new ThreeQuaternion();
    // Owns deterministic step-to-step travel; idle during free navigation.
    private _choreographer: CameraChoreographer = new CameraChoreographer();

    /** True, solange die Kamera programmatisch unterwegs ist — Commit-Gate
     *  fuer Konsumenten wie das Track-LOD: waehrend der Fahrt beobachten,
     *  erst am Ziel schalten.
     *
     *  Der Choreographer allein reicht NICHT: sein Preset defaultet auf
     *  'off', die Kamera faehrt dann sekundenlang ueber das klassische
     *  Lerp. Deshalb zaehlt zusaetzlich der reale Positionsrest zur
     *  Desired-Pose (Toleranz skaliert mit der Kameradistanz). Aendert
     *  keine Bewegung — macht nur den bestehenden Zustand beobachtbar. */
    public isTravelling(): boolean {
        if (this._pendingTransition || this._choreographer.isActive()) return true;
        if (!this._hatDesiredPose) return false;
        const toleranz = Math.max(0.0001, this.getEffectiveCameraDistance() * 0.01);
        if (this._camera.position.distanceTo(this._desiredPosition) <= toleranz) return false;
        /* Rest allein ist BLIND fuer externe Klemmungen (Terrain-Boden-
         * Kollision schiebt die Kamera dauerhaft von der Soll-Pose weg —
         * der Rest konvergiert dann nie und das Gate stuende fuer immer).
         * Reisen heisst: die Kamera BEWEGT sich auch tatsaechlich. */
        return this._lastMoveDelta > Math.max(1e-5, toleranz * 0.02);
    }
    /** Set by setTarget(), consumed on the next update() once the destination is known. */
    private _pendingTransition = false;
    /** Erst nach der ersten Desired-Pose ist der Positionsrest aussagekraeftig. */
    private _hatDesiredPose = false;
    private readonly _prevPosition: ThreeVector3 = new ThreeVector3();
    private _lastMoveDelta = 0;
    /** World position of the active POI — the point the camera looks at while travelling. */
    private _lookAtTarget = new ThreeVector3();
    /** Aktiver Annotations-Fokus: Kamera kreist um diesen Punkt statt ums POI-Zentrum. */
    private _annotationFocus: { pos: ThreeVector3; distance: number } | null = null;
    
    constructor(scene: Scene, settings: AppSettingsData, systemState: SystemState) {
        this._scene = scene;
        this._settings = settings;
        this._systemState = systemState;
        
        // This will be a universal camera, not tied to a specific POI on creation
        this._camera = new PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.01, 1000);
        // In three.js, we don't add the camera to the scene directly.
        // The renderer uses the scene and camera.
        
        // Setup responsive camera adjustments
        this.setupMobileAwareness();
    }
    
    /**
     * Setup mobile-aware camera behavior
     */
    private setupMobileAwareness(): void {
        // Listen for window resize to detect orientation/size changes
    }

    /**
     * Effective camera distance — uses the single `cameraDistance` setting and
     * adjusts internally on mobile so content stays fully visible on small viewports.
     * No separate mobile property: presentations define one `cameraDistance` value.
     */
    /**
     * Kamera auf einen Annotationspunkt der aktiven POI fokussieren (null = zurueck
     * zum POI-Zentrum). Nutzt denselben Bewegungspfad wie der POI-Wechsel — nur
     * Blickziel und Distanz aendern sich, das Hopping wirkt dadurch wie eine
     * kleine Kamerafahrt innerhalb des Objekts.
     */
    public setAnnotationFocus(worldPos: { x: number; y: number; z: number } | null, distance: number = 1.2): void {
        this._annotationFocus = worldPos
            ? { pos: new ThreeVector3(worldPos.x, worldPos.y, worldPos.z), distance }
            : null;
    }

    private getEffectiveCameraDistance(): number {
        if (this._annotationFocus) return this._annotationFocus.distance;
        const distance = this._settings.cameraDistance.value ?? 10;
        if (!DeviceInfo.isMobile) return distance;
        // Explizit gesetzte Mobil-Distanz gewinnt; der Multiplikator ist nur der
        // Fallback fuer Praesentationen ohne eigenen Wert. Der Markdown-Schluessel
        // mobileCameraDistance war lange ein Phantom — Autoren nutzten ihn bereits,
        // gewirkt hat er erst ab diesem Commit.
        const explicit = this._settings.mobileCameraDistance?.value ?? 0;
        return explicit > 0 ? explicit : distance * MOBILE_DISTANCE_MULTIPLIER;
    }
    
    // Track last target for one-shot logging when the update loop actually consumes a new POI.
    private _loggedTargetId: string | null = null;

    public update(deltaTime: number): void {
        if (!this._targetPOI || !this._camera) {
            return;
        }

        // ⚠️ FIX: Use poi.transform for camera tracking to avoid wiggle effects
        // The camera should follow the POI's logical position, not the animated shape
        // This prevents the camera from following wiggle animations and making other POIs appear to wiggle
        let targetTransform = this._targetPOI.transform;
        // Removed shape.transform usage for camera tracking to fix wiggle issue

        // Use getter methods that trigger updateWorldTransform() — reading worldPosition.value
        // directly returns the cached value which can be (0,0,0) if never updated.
        const targetPositionValue = targetTransform.getWorldPosition();
        const targetRotationValue = targetTransform.getWorldQuaternion();

        // One-shot log when a new POI first reaches the update loop.
        const targetId = this._targetPOI.name;
        if (targetId !== this._loggedTargetId) {
            this._loggedTargetId = targetId;
            Logger.Instance.Info(
                `update() consuming target=${targetId} ` +
                `wp=${targetPositionValue ? `(${targetPositionValue.x.toFixed(2)},${targetPositionValue.y.toFixed(2)},${targetPositionValue.z.toFixed(2)})` : 'null'} ` +
                `wr=${targetRotationValue ? 'ok' : 'null'} ` +
                `camPos=(${this._camera.position.x.toFixed(2)},${this._camera.position.y.toFixed(2)},${this._camera.position.z.toFixed(2)})`,
                'Camera'
            );
        }

        if (!targetPositionValue || !targetRotationValue) {
            return;
        }
        const targetPosition = new ThreeVector3(targetPositionValue.x, targetPositionValue.y, targetPositionValue.z);
        let targetRotation = new ThreeQuaternion(targetRotationValue.x, targetRotationValue.y, targetRotationValue.z, targetRotationValue.w);
        if (this._annotationFocus) {
            // Annotations-Fokus: Blickziel ist der Punkt, ABER der Kamera-Offset darf
            // NICHT mit der POI-Rotation gedreht werden. Beim 2D-Bild ist die POI-
            // Rotation ~Identitaet und faellt nicht auf; ein 3D-Modell steht auf der
            // Kugel gedreht — der mitgedrehte Offset setzt die Kamera seitlich/hinter
            // den Punkt, und die Fahrt zum naechsten MeshPoint kreist ums Modell
            // statt hinzuschauen (Befund Alex: 'das Netz bewegt sich mit').
            // Stattdessen: Offset entlang der AKTUELLEN Sichtachse Kamera->POI, damit
            // die Kamera aus ihrer jetzigen Richtung auf den Punkt heranfaehrt.
            targetPosition.copy(this._annotationFocus.pos);
            const viewDir = new ThreeVector3().subVectors(this._camera.position, targetPosition);
            viewDir.y = 0;
            if (viewDir.lengthSq() < 1e-6) viewDir.set(0, 0, 1);
            viewDir.normalize();
            // Quaternion, deren +Z auf viewDir zeigt (der Offset wird lokal entlang +Z gebaut)
            targetRotation = new ThreeQuaternion().setFromUnitVectors(new ThreeVector3(0, 0, 1), viewDir);
        }
        this._lookAtTarget.copy(targetPosition);
        
        const mode = this._settings.cameraRotationMode.value;

        switch (mode) {
            case CameraRotationMode.SMOOTH_TURN:
                this.updateSmoothTurn(deltaTime, targetPosition, targetRotation);
                break;
            case CameraRotationMode.DIRECT_AXIS:
                this.updateDirectAxis(deltaTime, targetPosition, targetRotation);
                break;
            case CameraRotationMode.FIXED_OFFSET:
                this.updateFixedOffset(deltaTime, targetPosition, targetRotation);
                break;
        }
        this._hatDesiredPose = true;
        this._lastMoveDelta = this._camera.position.distanceTo(this._prevPosition);
        this._prevPosition.copy(this._camera.position);
    }
    
    private updateSmoothTurn(deltaTime: number, targetPosition: ThreeVector3, targetRotation: ThreeQuaternion): void {
        const distance = this.getEffectiveCameraDistance();
        const latitude = (this._settings.cameraLatitude.value ?? 30) * (Math.PI / 180);
        const longitude = (this._settings.cameraLongitude.value ?? 45) * (Math.PI / 180);
        const translationSpeed = this._settings.cameraTranslationSpeed.value ?? 4;
        const rotationSpeed = this._settings.cameraRotationSpeed.value ?? 3;

        // 1. Calculate desired position using a local offset from spherical coordinates.
        const localTranslationOffset = new ThreeVector3(
            distance * Math.cos(latitude) * Math.sin(longitude),
            distance * Math.sin(latitude),
            distance * Math.cos(latitude) * Math.cos(longitude)
        );
        const worldOffset = localTranslationOffset.clone().applyQuaternion(targetRotation);
        this._desiredPosition.copy(targetPosition).add(worldOffset);
        
        // 2./4. Desired rotation is needed by BOTH paths, so compute it before deciding.
        // It is derived from the DESIRED position (not the current one) — otherwise the
        // look-at target lags a frame behind during a choreographed move.
        const cameraUp = new ThreeVector3(0, 1, 0).applyQuaternion(targetRotation);
        const rotationMatrix = new Matrix4().lookAt(this._desiredPosition, targetPosition, cameraUp);
        this._desiredQuaternion.setFromRotationMatrix(rotationMatrix);

        this.applyCameraMove(deltaTime, this._desiredPosition, distance, translationSpeed, rotationSpeed);
    }

    private updateDirectAxis(deltaTime: number, targetPosition: ThreeVector3, targetRotation: ThreeQuaternion): void {
        const distance = this.getEffectiveCameraDistance();
        const latitude = (this._settings.cameraLatitude.value ?? 30) * (Math.PI / 180);
        const longitude = (this._settings.cameraLongitude.value ?? 45) * (Math.PI / 180);
        const rotationSpeed = this._settings.cameraRotationSpeed.value ?? 3;

        // 1. Calculate the local offset vector from spherical coordinates
        const localTranslationOffset = new ThreeVector3(
            distance * Math.cos(latitude) * Math.sin(longitude),
            distance * Math.sin(latitude),
            distance * Math.cos(latitude) * Math.cos(longitude)
        );

        // 2. Apply the POI's rotation to get the world-space offset.
        const worldOffset = localTranslationOffset.clone().applyQuaternion(targetRotation);

        // 3. Calculate the final camera position and set it directly
        // (_desiredPosition mitfuehren, damit isTravelling() in allen Modi
        // wohldefiniert ist — hier ist der Rest per Konstruktion ~0)
        this._desiredPosition.copy(targetPosition).add(worldOffset);
        this._camera.position.copy(this._desiredPosition);
        
        // --- Smooth Rotation ---
        const cameraUp = new ThreeVector3(0, 1, 0).applyQuaternion(targetRotation);
        const rotationMatrix = new Matrix4().lookAt(this._camera.position, targetPosition, cameraUp);
        this._desiredQuaternion.setFromRotationMatrix(rotationMatrix);
        // Zweiter ungeklammerter Pfad (#1255): slerp mit t > 1 extrapoliert ueber das Ziel.
        this._camera.quaternion.slerp(this._desiredQuaternion, Math.min(1, rotationSpeed * deltaTime * 0.1));
    }

    private updateFixedOffset(deltaTime: number, targetPosition: ThreeVector3, targetRotation: ThreeQuaternion): void {
        const distance = this.getEffectiveCameraDistance();
        const latitude = (this._settings.cameraLatitude.value ?? 30) * (Math.PI / 180); // Convert to radians
        const longitude = (this._settings.cameraLongitude.value ?? 45) * (Math.PI / 180); // Convert to radians
        const translationSpeed = this._settings.cameraTranslationSpeed.value ?? 4;
        const rotationSpeed = this._settings.cameraRotationSpeed.value ?? 3;

        // 1. Calculate the local offset vector based on spherical coordinates
        const localTranslationOffset = new ThreeVector3(
            distance * Math.cos(latitude) * Math.sin(longitude),
            distance * Math.sin(latitude),
            distance * Math.cos(latitude) * Math.cos(longitude)
        );

        // 2. Apply the POI's world rotation to the local offset
        const worldOffset = localTranslationOffset.clone().applyQuaternion(targetRotation);
        
        // 3. Calculate the final desired camera position
        const desiredPosition = targetPosition.clone().add(worldOffset);

        // 4. Calculate the final desired camera rotation based on the FINAL position and the POI's "up" vector.
        const cameraUp = new ThreeVector3(0, 1, 0).applyQuaternion(targetRotation);
        const rotationMatrix = new Matrix4().lookAt(desiredPosition, targetPosition, cameraUp);
        this._desiredQuaternion.setFromRotationMatrix(rotationMatrix);

        // 5./6. Choreographed step travel when a step just changed, otherwise the
        // original smoothing (free navigation, wiggle tracking).
        this._desiredPosition.copy(desiredPosition);
        this.applyCameraMove(deltaTime, this._desiredPosition, distance, translationSpeed, rotationSpeed);
        // Der frueher hier stehende zweite Slerp war toter Code hinter `if (false)`; applyCameraMove uebernimmt die Rotation.
    }
    
    /** Starts a queued step transition once the destination pose is known. */
    private beginPendingTransition(distance: number): boolean {
        if (!this._pendingTransition) return false;
        this._pendingTransition = false;
        this._choreographer.begin(this._camera, this._desiredPosition, distance);
        return true;
    }

    /**
     * Single place where the camera actually moves: choreographed travel right after a
     * step change, otherwise the original asymptotic smoothing.
     */
    private applyCameraMove(
        deltaTime: number,
        desiredPosition: ThreeVector3,
        distance: number,
        translationSpeed: number,
        rotationSpeed: number
    ): void {
        this.beginPendingTransition(distance);
        if (this._choreographer.apply(this._camera, desiredPosition, this._desiredQuaternion, deltaTime, this._lookAtTarget)) return;
        /* Interpolationsfaktor KLAMMERN (#1255, Fix aus feature/terrain-kamera 383e503).
         * `Vector3.lerp(ziel, alpha)` rechnet `pos + (ziel - pos) * alpha`; fuer alpha > 1
         * schiesst das ueber das Ziel hinaus, bei translationSpeed 3 ab deltaTime > 3,3 s,
         * also bei jedem mehrsekuendigen Ruckler (Hintergrund-Tab, langsame GPU, Debugger).
         * Gemessen 2026-09-10 (Codex, /p/124859 auf arkserver): Kamera bei 1e10 Einheiten,
         * Soll-Pose durchgehend sauber. Nur die Ist-Pose entgleist. */
        this._camera.position.lerp(desiredPosition, Math.min(1, translationSpeed * deltaTime * 0.1));
        this._camera.quaternion.slerp(this._desiredQuaternion, Math.min(1, rotationSpeed * deltaTime * 0.1));
    }

    public setTarget(poi: POI): void {
        // Same target again (re-select) must not re-trigger a travel.
        if (this._targetPOI !== poi) this._pendingTransition = true;
        this._targetPOI = poi;
        // One-shot diagnostic: log what update() will read on the NEXT frame.
        // This catches the common "POI set but world transform not initialized" case.
        const t = poi.transform;
        const wp = t?.worldPosition?.value;
        const wr = t?.worldQuaternion?.value;
        const mode = this._settings.cameraRotationMode.value;
        const dist = this._settings.cameraDistance.value;
        const lat = this._settings.cameraLatitude.value;
        const lon = this._settings.cameraLongitude.value;
        Logger.Instance.Info(
            `setTarget(${poi.name}) — mode=${mode} dist=${dist} lat=${lat} lon=${lon} ` +
            `worldPos=${wp ? `(${wp.x.toFixed(2)},${wp.y.toFixed(2)},${wp.z.toFixed(2)})` : 'null'} ` +
            `worldRot=${wr ? 'ok' : 'null'}`,
            'Camera'
        );
    }
    
    public getTarget(): POI | null {
        return this._targetPOI;
    }
    
    public get camera(): Camera {
        return this._camera;
    }

    public get internalCamera(): PerspectiveCamera {
        return this._camera;
    }
} 